import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BruteForceGameAssets} from "../BruteForceGameAssets";
import {BruteForce} from "../BruteForce";
import {BLEND_MODES} from "pixi.js";
import {BruteForceHelper} from "../utils/BruteForceHelper";
import {gsap} from "gsap";
import {BruteForceSound} from "../sound/BruteForceSound";

export class BruteForceForegroundElements extends PIXI.Container implements StageLayer {

    private _reelLinesOverlay: PIXI.Sprite;
    private _arcadeMachine: PIXI.spine.Spine;
    private _nudgeCounterScreen: PIXI.spine.Spine;
    private _yellowGraphic: PIXI.Graphics;
    private _blueGraphic: PIXI.Graphics;
    private _redGraphic: PIXI.Graphics;
    private _yellowButton: PIXI.Sprite;
    private _blueButton: PIXI.Sprite;
    private _redButton: PIXI.Sprite;

    constructor() {
        super();
        this.name = "BruteForceForegroundElements";
        this.init();
    }

    private _yellowCount: number = 0;

    get yellowCount(): number {
        return this._yellowCount;
    }

    set yellowCount(value: number) {
        this._yellowCount = value;
    }

    private _blueCount: number = 0;

    get blueCount(): number {
        return this._blueCount;
    }

    set blueCount(value: number) {
        this._blueCount = value;
    }

    private _redCount: number = 0;

    get redCount(): number {
        return this._redCount;
    }

    set redCount(value: number) {
        this._redCount = value;
    }

    public playArcadeMachineAnimation(firstAnimName: string, toLoopFirstAnim: boolean, secondAnimName?: string, toLoopSecondAnim?: boolean): void {
        BruteForceHelper.resetSpineItem(this._arcadeMachine);
        if (secondAnimName) {
            this._arcadeMachine.state.setAnimation(0, firstAnimName, toLoopFirstAnim);
            this._arcadeMachine.state.setAnimation(1, secondAnimName, toLoopSecondAnim!);
        } else {
            this._arcadeMachine.state.setAnimation(0, firstAnimName, toLoopFirstAnim);
        }
    }

    public playNudgeCounterScreenAnimation(animationTabName: string, toLoopSpine: boolean): void {
        BruteForceHelper.resetSpineItem(this._nudgeCounterScreen);
        this._nudgeCounterScreen.state.setAnimation(0, animationTabName, toLoopSpine);
    }

    public setLinesOverlay(timeDur: number, setAlpha: number): gsap.core.Timeline {
        const linesTl: gsap.core.Timeline = gsap.timeline();
        linesTl.to(this._reelLinesOverlay, {duration: timeDur, alpha: setAlpha});
        return linesTl;
    }

    public setInteractiveButton(bool: boolean): void {
        const mode: string = BruteForce.model.data.mode;
        const nextMode: string = BruteForce.model.nextMode;
        if (mode.indexOf("FREESPIN") > -1 && nextMode.indexOf("FREESPIN") > -1) {
            this.buttonInteractionSettings(bool);
            return;
        }

        this.buttonInteractionSettings(bool);

        if (bool) {
            this._yellowGraphic.on("pointertap", () => {
                this._yellowButton.texture = BruteForce.assetLoader.getTextures("yellowButtonDown")[0];
                this.onClicked("yellow");
            });
            this._blueGraphic.on("pointertap", () => {
                this._blueButton.texture = BruteForce.assetLoader.getTextures("blueButtonDown")[0];
                this.onClicked("blue");
            });
            this._redGraphic.on("pointertap", () => {
                this._redButton.texture = BruteForce.assetLoader.getTextures("redButtonDown")[0];
                this.onClicked("red")
            });
        } else {
            this._yellowCount = 0;
            this._blueCount = 0;
            this._redCount = 0;
            this._yellowGraphic.off("pointertap");
            this._blueGraphic.off("pointertap");
            this._redGraphic.off("pointertap");
        }
    }

    public onClicked(type: string) {
        {
            let button : PIXI.Sprite;
            if(type === "red"){
                button = this._redButton;
            }
            else if(type === "blue"){
                button = this._blueButton;
            }
            else{
                button = this._yellowButton;
            }

            BruteForceSound.playEasterButtonSound(type);
            const clickTl: gsap.core.Timeline = gsap.timeline();
            clickTl.to(button,{duration:0.05});
            clickTl.add(()=>{
                if(type === "red"){
                    button.texture = BruteForce.assetLoader.getTextures("redButtonUp")[0];
                }
                else if(type === "blue"){
                    button.texture = BruteForce.assetLoader.getTextures("blueButtonUp")[0];
                }
                else{
                    button.texture = BruteForce.assetLoader.getTextures("yellowButtonUp")[0];
                }
                clickTl.kill();
            })


            switch (type) {
                case "yellow":
                    if (this._blueCount === 0 && this._redCount === 1) {
                        this._yellowCount++;
                    } else {
                        this.buttonCountReset();
                    }
                    break;

                case "blue":
                    if (this._redCount === 1 && this._yellowCount === 2) {
                        this._blueCount++;
                    } else {
                        this.buttonCountReset();
                    }
                    break;

                case "red":
                    if (this._blueCount === 0 && this._yellowCount === 0) {
                        this._redCount++;
                    } else {
                        this.buttonCountReset();
                    }
                    break;
            }

            if (this._redCount === 1 && this._yellowCount === 2 && this._blueCount === 4) {
                this.buttonCountReset();

                const tl: gsap.core.Timeline = gsap.timeline({
                    onStart: ()=>{
                        BruteForceSound.playTrack(BruteForceSound.EASTER_TILT);
                    },
                    onComplete: () => {
                        this.removeChild(easterEggText);
                        this.buttonInteractionSettings(true);
                        tl.kill();
                    }
                });
                const easterEggText: PIXI.Sprite = new PIXI.Sprite(BruteForce.assetLoader.getTextures("easterEgg")[0]);
                easterEggText.position.set(360, 320);
                easterEggText.anchor.set(0.5, 0.5);
                easterEggText.alpha = 0;

                const glitch : PIXI.spine.Spine = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("glitchScreen"));
                glitch.position.set(360, 360);
                glitch.alpha = 0;

                tl.add(() => {
                    this.addChild(glitch, easterEggText);
                    this.buttonInteractionSettings(false);
                    glitch.state.setAnimation(0,"loop", true);
                }, 0);

                tl.add(BruteForce.gameContainer.screenShake(1, 100, false, 30), 0);
                tl.to(glitch,{alpha:1, duration:0.1},0);
                tl.to(easterEggText, {alpha: 1, duration: 0.1}, "<0.5");
                tl.to(easterEggText, {duration: 0.3});
                tl.to([easterEggText, glitch], {alpha: 0, duration: 0.1});
            } else if (this._redCount > 2 || this._yellowCount > 2 || this._blueCount > 4) {
                this.buttonCountReset();
            }
        }
    }

    private init() {

        this._yellowButton = new PIXI.Sprite(BruteForce.assetLoader.getTextures("yellowButtonUp")[0]);
        this._yellowButton.position.set(619, 757);
        this._yellowButton.anchor.set(0.5, 0.5);
        this._yellowButton.name = "yellowButton";

        this._redButton = new PIXI.Sprite(BruteForce.assetLoader.getTextures("redButtonUp")[0]);
        this._redButton.position.set(179, 757);
        this._redButton.anchor.set(0.5, 0.5);
        this._redButton.name = "redButton";

        this._blueButton = new PIXI.Sprite(BruteForce.assetLoader.getTextures("blueButtonUp")[0]);
        this._blueButton.position.set(688, 755);
        this._blueButton.anchor.set(0.5, 0.5);
        this._blueButton.name = "blueButton";

        this._reelLinesOverlay = new PIXI.Sprite(BruteForce.assetLoader.getTextures(BruteForceGameAssets.SCREEN_LINES_TEXTURE)[0]);
        this._reelLinesOverlay.anchor.set(0.5, 0.5);
        this._reelLinesOverlay.position.set(360, 300);
        this._reelLinesOverlay.scale.set(1.2, 1.2);
        this._reelLinesOverlay.blendMode = BLEND_MODES.MULTIPLY;
        this._reelLinesOverlay.alpha = 0.2;
        this._reelLinesOverlay.name = "ScreenLinesOverlay";
        this.addChild(this._reelLinesOverlay);

        this._arcadeMachine = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("arcadeMachine"));
        this._arcadeMachine.position.set(355, 378);
        this._arcadeMachine.name = "arcadeMachine";
        // this._arcadeMachine.scale.set(1.2, 1.2);
        //  this._arcadeMachine.state.setAnimation(0, "normal", false);
        this.addChild(this._arcadeMachine);

        this._yellowGraphic = new PIXI.Graphics;
        this._yellowGraphic.beginFill(0xffffff, 1);
        this._yellowGraphic.drawRect(590, 725,60,60);
        this._yellowGraphic.endFill();
        this._yellowGraphic.alpha = 0;
        this._yellowGraphic.name = "yellowGraphic";
        this.addChild(this._yellowButton,this._yellowGraphic);

        this._blueGraphic = new PIXI.Graphics();
        this._blueGraphic.beginFill(0xffffff, 1);
        this._blueGraphic.drawRect(658, 725, 60, 60);
        this._blueGraphic.endFill();
        this._blueGraphic.alpha = 0;
        this._blueGraphic.name = "blueGraphic";
        this.addChild(this._blueButton, this._blueGraphic);

        this._redGraphic = new PIXI.Graphics();
        this._redGraphic.beginFill(0xffffff, 1);
        this._redGraphic.drawRect(150, 725, 60, 60);
        this._redGraphic.endFill();
        this._redGraphic.alpha = 0;
        this._redGraphic.name = "redGraphic";
        this.addChild(this._redButton, this._redGraphic);
    }

    private buttonInteractionSettings(bool: boolean) {
        this._yellowGraphic.interactive = bool;
        this._blueGraphic.interactive = bool;
        this._redGraphic.interactive = bool;

        this._yellowGraphic.buttonMode = bool;
        this._blueGraphic.buttonMode = bool;
        this._redGraphic.buttonMode = bool;
    }

    public buttonCountReset() {
        this._yellowCount = 0;
        this._redCount = 0;
        this._blueCount = 0;
    }
}